import React, { useState } from "react";
import ReactTable from "react-table";
import { useTranslation } from "react-i18next";
import range from "lodash/range";
import { useDeleteEtude } from "./useEtude";
import Notifications from "react-notification-system-redux";
import { translations } from "helpers/react-table";
import { useDispatch } from "react-redux";

const notifyError = (t) =>
  Notifications.error({
    title: t("etudes.delete.error"),
    position: "bl",
    autoDismiss: 3,
  });

const getPageSizeOptions = (total) => {
  if (!total || total === 0) return [10, 15, 20];

  const baseOptions = [10, 15].concat(
    range(7)
      .map((i) => i * 5 + 20)
      .filter((n) => n < total),
  );

  return Array.from(new Set([...baseOptions, total]));
};

const DeleteCell = ({ onClick }) => (
  <div
    onClick={onClick}
    css={{
      textAlign: "center",
      opacity: 0.7,
      cursor: "pointer",
      "&:hover": { opacity: 1 },
    }}
  >
    <i className="icon-ico-trash" />
  </div>
);

export const EtudesTable = ({
  studies,
  isFetching,
  onRowClick,
  selectedStudyId,
}) => {
  const { t } = useTranslation();
  const ref = React.useRef(null);
  const [selectedRows, setSelectedRows] = useState({});

  const [deleteEtude, { isLoading: isDeleting }] = useDeleteEtude();
  const dispatch = useDispatch();

  const data = React.useMemo(
    () =>
      !studies || studies.length === 0
        ? []
        : Object.values(studies).map((study) => ({
            id: study.Id,
            name: study.Name,
            initialSubscription: study.InitialSubscription || "-",
            targetSubscription: study.TargetSubscription || "-",
            status: study.Status || "-",
          })),
    [studies],
  );

  const toggleRow = (id) => {
    setSelectedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const formatSubscription = (value) => {
    if (value === null || value === undefined) return "";
    switch (value) {
      case 1:
        return t("etudes.subscriptionType.1");
      case 2:
        return t("etudes.subscriptionType.2");
      case 3:
        return t("etudes.subscriptionType.3");
      default:
        return value;
    }
  };

  const formatStatus = (value) => {
    if (value === null || value === undefined) return "";
    switch (value) {
      case 1:
        return t("etudes.status.1");
      case 2:
        return t("etudes.status.2");
      case 3:
        return t("etudes.status.3");
      case 4:
        return t("etudes.status.4");
      default:
        return value;
    }
  };

  const toggleAll = () => {
    const isAllSelected =
      Object.keys(selectedRows).filter((k) => selectedRows[k]).length ===
      data.length;

    const newSelectedRows = {};
    if (!isAllSelected) {
      data.forEach((row) => {
        newSelectedRows[row.id] = true;
      });
    }
    setSelectedRows(newSelectedRows);
  };

  const isAllSelected =
    data.length > 0 &&
    Object.keys(selectedRows).filter((k) => selectedRows[k]).length ===
      data.length;

  const columns = React.useMemo(
    () =>
      [
        {
          Header: t("etudes.table.name"),
          accessor: "name",
        },
        {
          Header: t("etudes.table.initialSubscription"),
          accessor: "initialSubscription",
          Cell: ({ value }) => formatSubscription(value),
        },
        {
          Header: t("etudes.table.targetSubscription"),
          accessor: "targetSubscription",
          Cell: ({ value }) => formatSubscription(value),
        },
        {
          Header: t("etudes.table.status"),
          accessor: "status",
          Cell: ({ value }) => formatStatus(value),
        },
        {
          accessor: "id",
          filterable: false,
          width: 40,
          Cell: ({ value }) => (
            <DeleteCell
              onClick={(e) => {
                e.stopPropagation(); // prevent bubbling to row click

                if (window.confirm(t("etudes.delete.confirm"))) {
                  deleteEtude(value, {
                    onError: (error) => {
                      dispatch(notifyError(t));
                      console.error(error);
                    },
                  });
                }
              }}
            />
          ),
        },
      ].map((column) =>
        column.filterable === false
          ? column
          : {
              ...column,
            },
      ),
    [t, selectedRows, data, isAllSelected, deleteEtude],
  );

  return (
    <>
      <ReactTable
        ref={ref}
        data={data}
        columns={columns}
        defaultPageSize={5}
        loading={isFetching || isDeleting}
        pageSizeOptions={getPageSizeOptions(data.length)}
        getTrProps={(state, rowInfo) => {
          if (rowInfo && rowInfo.original) {
            const isSelected = rowInfo.original.id === selectedStudyId;

            return {
              onClick: () => {
                if (onRowClick) {
                  onRowClick(rowInfo.original.id);
                }
              },
              style: {
                cursor: "pointer",
              },
              className: isSelected ? "-selected" : "",
            };
          }
          return {
            style: { cursor: "unset" },
          };
        }}
        {...translations(t)}
        className="shadow-sm -striped -highlight"
        css={{
          fontSize: 13,
          ".rt-thead.-filters .rt-th": {
            overflow: "unset",
          },
          ".-selected": {
            backgroundColor: "rgba(0, 123, 255, 0.1) !important",
          },
        }}
        filterable
      />
    </>
  );
};
